// src/components/ModelSelector.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Settings, X, Search, Filter, Check } from 'lucide-react';
import './ModelSelector.css';

const ModelSelector = ({ selectedModel, onModelSelect, hasAttachments, userBalance = 1.0, mode = 'chat' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allModels, setAllModels] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [sessionStats, setSessionStats] = useState({ isBlocked: false, balance: userBalance });
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const hasInsufficientBalance = userBalance < 0.001;
  const isSessionBlocked = sessionStats.isBlocked || hasInsufficientBalance;
  const selectedModelData = availableModels.find(m => m.id === selectedModel) || 
    allModels.find(m => m.id === selectedModel) || 
    availableModels.find(m => m.displayName === 'Dali L3attar') ||
    availableModels.find(m => m.id === defaultModel) ||
    availableModels[0] || { displayName: 'Loading...', name: '' };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleSessionUpdate = () => {
      const stats = JSON.parse(localStorage.getItem('sessionStats') || '{}');
      setSessionStats({ 
        isBlocked: stats.isBlocked || false, 
        balance: stats.balance || userBalance 
      });
    };
    
    const handleUserStatsUpdate = (event) => {
      setSessionStats(prev => ({ 
        ...prev, 
        balance: event.detail.balance 
      }));
    };
    
    window.addEventListener('sessionStatsUpdated', handleSessionUpdate);
    window.addEventListener('userStatsUpdated', handleUserStatsUpdate);
    handleSessionUpdate();
    
    return () => {
      window.removeEventListener('sessionStatsUpdated', handleSessionUpdate);
      window.removeEventListener('userStatsUpdated', handleUserStatsUpdate);
    };
  }, [userBalance]);
  
  useEffect(() => {
    fetchModels();
  }, [mode]);

  useEffect(() => {
    if (showAdvanced) {
      fetchAllModels();
    }
  }, [showAdvanced]);
  
  useEffect(() => {
    fetchModels();
  }, [isSessionBlocked]);

  const fetchModels = async () => {
    try {
      const cacheKey = `modelSelector_${mode}_${isSessionBlocked}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(`${cacheKey}_time`);
      
      if (cached && cacheTime && Date.now() - parseInt(cacheTime) < 60000) {
        const cachedData = JSON.parse(cached);
        setAvailableModels(cachedData.models);
        setDefaultModel(cachedData.defaultModel);
        return;
      }
      
      const endpoint = mode === 'vibecoding' ? '/api/models/vibecoding' : '/api/models/chat';
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}${endpoint}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      const models = isSessionBlocked 
        ? data.models.filter(model => model.free) 
        : data.models;
      
      localStorage.setItem(cacheKey, JSON.stringify({ models, defaultModel: data.defaultModel }));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      
      setAvailableModels(models);
      setDefaultModel(data.defaultModel || models[0]?.id);
      
      if (isSessionBlocked && selectedModel) {
        const currentModelAvailable = models.find(m => m.id === selectedModel);
        if (!currentModelAvailable) {
          const freeModel = models.find(m => m.free);
          if (freeModel && onModelSelect) {
            onModelSelect(freeModel.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      setAvailableModels([]);
    }
  };

  const fetchAllModels = async () => {
    if (allModels.length > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}/api/models/all`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      const filteredModels = (data.models || []).filter(model => {
        const modelId = model.id.toLowerCase();
        const problematicPatterns = [
          'exp', 'preview', 'beta', 'alpha', 'test', 'dev', 'experimental'
        ];
        return !problematicPatterns.some(pattern => modelId.includes(pattern));
      });
      
      setAllModels(filteredModels);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (modelId) => {
    onModelSelect(modelId);
    setIsOpen(false);
    setShowAdvanced(false);
  };

  const openAdvancedSelector = () => {
    setIsOpen(false);
    setShowAdvanced(true);
  };

  const formatPrice = (pricing, currency = 'USD') => {
    if (!pricing || (pricing.input === 0 && pricing.output === 0) || 
        (pricing.prompt === '0' && pricing.completion === '0')) {
      return 'Free';
    }
    const inputPrice = pricing.input || parseFloat(pricing.prompt || 0);
    if (inputPrice === 0) return 'Free';
    
    const symbol = currency === 'TND' ? 'DT' : '$';
    const multiplier = currency === 'TND' ? 3 : 1;
    const price = (inputPrice * multiplier).toFixed(currency === 'TND' ? 2 : 4);
    
    return `${symbol}${price}/1M`;
  };

  const getFilteredAdvancedModels = () => {
    let filtered = [...allModels];
    
    if (searchTerm) {
      filtered = filtered.filter(model => 
        model.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.slice(0, 50);
  };

  return (
    <>
      <div className="model-selector-v2" ref={dropdownRef}>
        <div 
          className="model-selector-trigger-v2"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="selected-model-v2">
            <div className="model-info-display">
              <span className="model-name">{selectedModelData?.displayName || selectedModelData?.name}</span>
            </div>
            {selectedModelData?.free && (
              <span className="free-badge">Free</span>
            )}
            {isSessionBlocked && (
              <span className="balance-warning">
                {sessionStats.isBlocked ? 'Session Limit' : 'Low Balance'}
              </span>
            )}
          </div>
          <div className="dropdown-controls">
            <button 
              className="advanced-btn"
              onClick={(e) => {
                e.stopPropagation();
                openAdvancedSelector();
              }}
              title="Advanced Model Selector"
            >
              <Settings size={16} />
            </button>
            <ChevronDown 
              size={16} 
              className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
            />
          </div>
        </div>

        {isOpen && (
          <div className={`simple-model-dropdown ${dropdownPosition === 'top' ? 'dropdown-top' : 'dropdown-bottom'}`}>
            <div className="simple-models-list">
              {availableModels.map((model) => (
                <div
                  key={model.id}
                  className={`simple-model-item ${model.id === selectedModel ? 'selected' : ''}`}
                  onClick={() => handleModelSelect(model.id)}
                >
                  <div className="model-info">
                    <div className="model-name">
                      {model.displayName}
                      <div className="model-capabilities">
                        {model.capabilities?.vision && (
                          <span className="capability-badge vision" title="Accepts images as input">
                            👁️
                          </span>
                        )}
                        {model.capabilities?.coding && (
                          <span className="capability-badge coding" title="Specialized for coding tasks">
                            💻
                          </span>
                        )}
                        {model.capabilities?.reasoning && (
                          <span className="capability-badge reasoning" title="Advanced reasoning and analysis">
                            🧠
                          </span>
                        )}
                        {model.capabilities?.multimodal && (
                          <span className="capability-badge multimodal" title="Handles multiple input types">
                            🔄
                          </span>
                        )}
                        {model.free && (
                          <span className="capability-badge free" title="Free to use">
                            🆓
                          </span>
                        )}
                        {model.acceptedFiles?.includes('pdf') && (
                          <span className="capability-badge pdf" title="Accepts PDF files">
                            📄
                          </span>
                        )}
                        {model.contextLength >= 250000 && (
                          <span className="capability-badge large-context" title="Large context window">
                            📚
                          </span>
                        )}
                        {(model.realPricing?.input <= 0.1 && model.realPricing?.output <= 0.3) && !model.free && (
                          <span className="capability-badge cheap" title="Low cost model">
                            💰
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="model-desc">{model.description}</div>
                  </div>
                  <div className="model-actions">
                    <div className="model-pricing">
                      {formatPrice(model.pricing)}
                    </div>
                    {model.id === selectedModel && (
                      <Check size={16} className="selected-check" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {isSessionBlocked && (
              <div className="balance-notice" style={{ padding: '12px', background: 'rgba(255, 193, 7, 0.2)', border: '1px solid rgba(255, 193, 7, 0.5)', borderRadius: '8px', marginTop: '8px' }}>
                <p style={{ margin: '0', color: '#fff', fontSize: '14px', textAlign: 'center' }}>⚠️ {sessionStats.isBlocked ? 'Session limit reached' : 'Low balance'} - only free models available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showAdvanced && ReactDOM.createPortal(
        <div className="model-modal-overlay" onClick={() => setShowAdvanced(false)}>
          <div className="model-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Advanced Model Selector</h3>
              <button className="close-btn" onClick={() => setShowAdvanced(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="search-section">
              <div className="search-input-container">
                <Search size={18} className="search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button className="clear-search" onClick={() => setSearchTerm('')}>
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="models-grid">
              {loading ? (
                <div className="loading-models">Loading models...</div>
              ) : getFilteredAdvancedModels().length === 0 ? (
                <div className="no-models">No models found</div>
              ) : (
                getFilteredAdvancedModels().map((model) => (
                  <div
                    key={model.id}
                    className={`model-card ${model.id === selectedModel ? 'selected' : ''}`}
                    onClick={() => handleModelSelect(model.id)}
                  >
                    <div className="model-card-header">
                      <div className="model-info">
                        <div className="model-name">{model.name}</div>
                        <div className="model-id">{model.id}</div>
                      </div>
                      <div className="model-price">
                        {formatPrice(model.pricing)}
                      </div>
                    </div>
                    {model.description && (
                      <div className="model-description">
                        {model.description.slice(0, 100)}...
                      </div>
                    )}
                    <div className="model-stats">
                      {model.context_length && (
                        <span className="context-length">
                          {(model.context_length / 1000).toFixed(0)}K context
                        </span>
                      )}
                      {model.id === selectedModel && (
                        <span className="selected-indicator">
                          <Check size={14} /> Selected
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ModelSelector;